import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { assertNever, ProspectListEnrichmentState } from '@signalvote/shared';
import type {
  ProspectListConfirmRequest,
  ProspectListConfirmResponse,
  ProspectListDetectResponse,
  ProspectListPreviewRequest,
  ProspectListPreviewResponse,
  ProspectListRead,
  ProspectListRetryRequest,
  ProspectListUndoPreview,
  ProspectListUndoRequest,
} from '@signalvote/shared';
import { authService } from '../../services/authService';
import { apiJson, apiResponse, type ApiErrorBody } from './apiFetch';
import { queryKeys } from '../../lib/queryKeys';
import { isTerminalStatus, readSseEvents } from '../../lib/fundraising/prospectListUpload';
import { useCampaignStore } from '../../stores';

const BASE_PATH = '/api/fundraising/prospect-lists';

/** A non-ok response as an Error: the server's sentence (`error`, else `message`; a JSON `null` body reaches here as null), else `fallback`. */
const messageOr = (fallback: string) => (body: ApiErrorBody | null) => new Error(body?.error ?? body?.message ?? fallback);

function postJson<T>(path: string, body: unknown, fallback: string): Promise<T> {
  return apiJson<T>(`${BASE_PATH}/${path}`, { method: 'POST', json: body, error: messageOr(fallback) });
}

/** Store the file and get its columns with suggested bindings. Writes no list. */
export function useDetectProspectList() {
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append('file', file);
      // No Content-Type: fetch sets the multipart boundary itself.
      return apiJson<ProspectListDetectResponse>(`${BASE_PATH}/detect`, {
        method: 'POST',
        body: form,
        error: messageOr('We could not read that file.'),
      });
    },
  });
}

/** Count the whole file under a set of bindings — the gate a person confirms against. Writes nothing. */
export function usePreviewProspectList() {
  return useMutation({
    mutationFn: (request: ProspectListPreviewRequest) =>
      postJson<ProspectListPreviewResponse>('preview', request, 'We could not check the file.'),
  });
}

/** Create the list and queue its upload. */
export function useConfirmProspectList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: ProspectListConfirmRequest) =>
      postJson<ProspectListConfirmResponse>('confirm', request, 'We could not start the upload.'),
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.fundraising.prospectLists() }),
  });
}

const UNFINISHED_POLL_MS = 3000;

/**
 * The uploads this tab is following over their progress stream, per query client (so a fresh client starts
 * clean), with how many streams follow each — a remount can open the next before the last has closed. An open
 * stream is what moves an upload's progress; the list of uploads needn't be re-read for it.
 */
interface OpenStreams {
  counts: Map<string, number>;
  ids: ReadonlySet<string>;
  listeners: Set<() => void>;
}
const openStreamsByClient = new WeakMap<QueryClient, OpenStreams>();
function openStreamsOf(queryClient: QueryClient): OpenStreams {
  let streams = openStreamsByClient.get(queryClient);
  if (!streams) openStreamsByClient.set(queryClient, (streams = { counts: new Map(), ids: new Set(), listeners: new Set() }));
  return streams;
}
function countStream(queryClient: QueryClient, id: string, change: 1 | -1) {
  const streams = openStreamsOf(queryClient);
  const count = (streams.counts.get(id) ?? 0) + change;
  if (count > 0) streams.counts.set(id, count);
  else streams.counts.delete(id);
  if (streams.counts.has(id) === streams.ids.has(id)) return;
  streams.ids = new Set(streams.counts.keys());
  for (const listener of streams.listeners) listener();
}
function useOpenStreams(): ReadonlySet<string> {
  const queryClient = useQueryClient();
  const subscribe = useCallback(
    (onChange: () => void) => {
      const { listeners } = openStreamsOf(queryClient);
      listeners.add(onChange);
      return () => listeners.delete(onChange);
    },
    [queryClient],
  );
  return useSyncExternalStore(subscribe, () => openStreamsOf(queryClient).ids);
}

function prospectListsQuery() {
  return {
    queryKey: queryKeys.fundraising.prospectLists(),
    queryFn: () => apiJson<ProspectListRead[]>(BASE_PATH, { error: 'Failed to fetch prospect lists' }),
  };
}

/**
 * The campaign's uploads, newest first. An upload still adding people moves over its progress stream
 * (`useProspectListProgress`); only while one is unfinished with no stream open — a retried list nobody is
 * following, or a stream that dropped — is the list re-read every few seconds instead.
 */
export function useProspectLists() {
  const { selectedCampaign } = useCampaignStore();
  const streaming = useOpenStreams();
  return useQuery({
    ...prospectListsQuery(),
    enabled: authService.isAuthenticated() && !!selectedCampaign,
    refetchInterval: (query) =>
      query.state.data?.some((list) => !isTerminalStatus(list.status) && !streaming.has(list.id)) ? UNFINISHED_POLL_MS : false,
  });
}

/**
 * The campaign's uploads as the book's filters name them. Reads the same list as `useProspectLists` but changes
 * only when a name or a status does, so an upload's progress never re-renders the book.
 */
export function useProspectListNames() {
  const { selectedCampaign } = useCampaignStore();
  return useQuery({
    ...prospectListsQuery(),
    enabled: authService.isAuthenticated() && !!selectedCampaign,
    select: (lists): Pick<ProspectListRead, 'id' | 'name' | 'status'>[] => lists.map(({ id, name, status }) => ({ id, name, status })),
  });
}

/**
 * Carry on a failed upload from where it stopped. The list reads Pending, then uploads again. Not
 * optimistic: the returned token goes stale as soon as the job moves the list, so the refreshed list
 * (and its polling) is the source of the next token.
 */
export function useRetryProspectList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...request }: ProspectListRetryRequest & { id: string }) =>
      postJson<ProspectListConfirmResponse>(`${id}/retry`, request, 'We could not retry the upload.'),
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.fundraising.prospectLists() }),
  });
}

/**
 * Who undoing the list would remove and keep, read when the confirmation opens. Always refetched on
 * open, since a call made meanwhile moves the counts.
 */
export function useProspectListUndoPreview(prospectListId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.fundraising.prospectListUndoPreview(prospectListId),
    queryFn: () =>
      apiJson<ProspectListUndoPreview>(`${BASE_PATH}/${prospectListId}/undo-preview`, {
        error: messageOr('We could not check what undo would do.'),
      }),
    enabled,
    staleTime: 0,
    gcTime: 0,
  });
}

/** Undo a list. Returns the list as undone, with what the undo removed and kept. */
export function useUndoProspectList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...request }: ProspectListUndoRequest & { id: string }) =>
      postJson<ProspectListRead>(`${id}/undo`, request, 'We could not undo the list.'),
    onSettled: () => {
      // Just the lists: the undo preview sits under the same prefix, and reading it again for a list that is
      // now undone is refused.
      void queryClient.invalidateQueries({ queryKey: queryKeys.fundraising.prospectLists(), exact: true });
      // Undo takes the list's people off the book.
      void queryClient.invalidateQueries({ queryKey: queryKeys.fundraising.prospects() });
    },
  });
}

/** True once an upload's matching has started — it may already have finished. */
function matchingStarted(state: ProspectListEnrichmentState | undefined): boolean {
  switch (state) {
    case undefined:
    case ProspectListEnrichmentState.Waiting:
    case ProspectListEnrichmentState.NotRun:
      return false;
    case ProspectListEnrichmentState.Running:
    case ProspectListEnrichmentState.Completed:
    case ProspectListEnrichmentState.Failed:
      return true;
    default:
      return assertNever(state);
  }
}

/** True while a finished upload's people are still being matched; the progress stream stays open until not. */
function matchingUnderway(state: ProspectListEnrichmentState | undefined): boolean {
  switch (state) {
    case ProspectListEnrichmentState.Waiting:
    case ProspectListEnrichmentState.Running:
      return true;
    case undefined:
    case ProspectListEnrichmentState.Completed:
    case ProspectListEnrichmentState.Failed:
    case ProspectListEnrichmentState.NotRun:
      return false;
    default:
      return assertNever(state);
  }
}

/**
 * One upload's progress, streamed with fetch + ReadableStream (not EventSource, so the auth header goes
 * along). The stream closes once the list reaches a terminal status and its people are matched; the list
 * query and the book are refreshed when the upload ends and again when matching does.
 */
export function useProspectListProgress(prospectListId: string | null) {
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState<ProspectListRead | null>(null);

  useEffect(() => {
    setProgress(null);
    if (!prospectListId) return;
    const controller = new AbortController();
    let opened = false;

    void (async () => {
      try {
        const response = await apiResponse(`${BASE_PATH}/${prospectListId}/progress`, { signal: controller.signal });
        if (!response.ok || !response.body) return;
        opened = true;
        countStream(queryClient, prospectListId, 1);

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let toldRunCard = false;
        let readOnceImported = false;
        let readOnceMatched = false;
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          const { events, rest } = readSseEvents<ProspectListRead>(buffer + decoder.decode(value, { stream: true }));
          buffer = rest;
          const latest = events.at(-1);
          if (latest) {
            setProgress(latest);
            if (!toldRunCard && matchingStarted(latest.enrichment?.state)) {
              toldRunCard = true;
              // Matching the upload's people shows as the campaign's run; the run card picks it up. Also
              // on an end state: matching that finishes inside one tick of the stream is never seen running.
              void queryClient.invalidateQueries({ queryKey: queryKeys.fundraising.enrichmentRun() });
            }
            // The stream stays open after the upload while its people are matched, one event a second. The
            // book is re-read once when the upload ends, so its people show, and once more when matching ends,
            // so their giving fills in — never on the ticks between.
            if (isTerminalStatus(latest.status)) {
              const matched = !matchingUnderway(latest.enrichment?.state);
              if (matched ? !readOnceMatched : !readOnceImported) {
                readOnceImported = true;
                readOnceMatched = matched;
                void queryClient.invalidateQueries({ queryKey: queryKeys.fundraising.prospectLists() });
                void queryClient.invalidateQueries({ queryKey: queryKeys.fundraising.prospects() });
              }
            }
          }
        }
      } catch {
        // Aborted on unmount, or the stream dropped: the list query still shows where the upload stands.
      } finally {
        // Closed: an upload it left unfinished goes back to being re-read on the list's timer.
        if (opened) countStream(queryClient, prospectListId, -1);
      }
    })();

    return () => controller.abort();
  }, [prospectListId, queryClient]);

  return progress;
}
